import { useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';

type Prospect = {
  name: string; address?: string | null; phone?: string | null; website?: string | null;
  rating?: number | null; source: string; origin: string; score?: number | null;
  rationale?: string | null; approach?: string | null;
};

export default function Prospeccao() {
  const [bairro, setBairro] = useState('');
  const [nicho, setNicho] = useState('');
  const [prompt, setPrompt] = useState('');
  const [source, setSource] = useState<'google_maps' | 'linkedin'>('google_maps');
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [mode, setMode] = useState<'live' | 'demo' | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [interpreted, setInterpreted] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  async function onSearch(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post<{ prospects: Prospect[]; mode: 'live' | 'demo'; warning?: string; interpreted?: { query: string; note: string | null } | null }>('/prospection/search', { bairro, nicho: nicho || undefined, prompt: prompt || undefined, source });
      setProspects(r.prospects);
      setMode(r.mode);
      setWarning(r.warning ?? null);
      setInterpreted(r.interpreted?.query ?? null);
      setSelected([]);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha na busca');
    } finally {
      setBusy(false);
    }
  }

  async function onImport() {
    if (!selected.length) return;
    setBusy(true);
    try {
      const r = await api.post<{ created: number; duplicated: number }>('/prospection/import', {
        prospects: prospects.filter((p) => selected.includes(p.name)),
      });
      toast.success(`${r.created} leads criados, ${r.duplicated} duplicados.`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha no import');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-4xl">
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Captação</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Prospecção</h1>
        <p className="mt-1 text-sm text-[var(--text-dim)]">Descreva onde buscar; a IA monta a consulta, busca empresas e pontua o potencial.</p>
      </header>
      <form onSubmit={onSearch} className="mt-4 grid gap-3 rounded-lg border border-[var(--border)] bg-white p-4 md:grid-cols-[1fr_1fr_auto_auto]">
        <input value={bairro} onChange={(e) => setBairro(e.target.value)} required placeholder="Bairro ou cidade (ex: Centro, Rio de Janeiro)"
          className="h-10 rounded-lg border border-[var(--border-strong)] px-3 text-sm" />
        <input value={nicho} onChange={(e) => setNicho(e.target.value)} placeholder="Nicho (ex: logística, clínicas)"
          className="h-10 rounded-lg border border-[var(--border-strong)] px-3 text-sm" />
        <select value={source} onChange={(e) => setSource(e.target.value as 'google_maps' | 'linkedin')} className="h-10 rounded-lg border border-[var(--border-strong)] px-2 text-sm">
          <option value="google_maps">Google Maps</option>
          <option value="linkedin">LinkedIn</option>
        </select>
        <button type="submit" disabled={busy} className="h-10 rounded-lg bg-[var(--accent)] px-5 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
          {busy ? 'Buscando...' : 'Buscar'}
        </button>
      </form>
      {interpreted && <p className="mt-2 text-xs text-[var(--text-dim)]">Consulta gerada: "{interpreted}"</p>}
      {warning && <p className="mt-2 text-xs text-[var(--text-dim)]">{warning}</p>}
      {prospects.length > 0 && (
        <section className="mt-4">
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Resultados ({prospects.length}) {mode === 'demo' && <span className="text-[var(--text-dim)]">· exemplo demo</span>}</h2>
            <button onClick={onImport} disabled={busy || selected.length === 0} className="h-9 rounded-lg bg-[var(--accent)] px-4 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
              Importar selecionados ({selected.length})
            </button>
          </header>
          <ul className="mt-2 divide-y divide-[var(--border)] rounded-lg border border-[var(--border)] bg-white text-sm">
            {prospects.map((p) => (
              <li key={p.name} className="flex items-start gap-3 px-4 py-3">
                <input type="checkbox" checked={selected.includes(p.name)} onChange={() => setSelected((s) => s.includes(p.name) ? s.filter((x) => x !== p.name) : [...s, p.name])} className="mt-1" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{p.name}</span>
                    {p.score != null && <span className="rounded bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-semibold">score {p.score}</span>}
                    <span className="text-xs text-[var(--text-dim)]">{p.source}{p.origin === 'demo' ? ' (demo)' : ''}</span>
                  </div>
                  <p className="text-xs text-[var(--text-dim)]">{[p.address, p.phone].filter(Boolean).join(' · ')}</p>
                  {p.rationale && <p className="mt-1 text-xs">{p.rationale}</p>}
                  {p.approach && <p className="mt-0.5 text-xs text-[var(--text-dim)]">Abordagem: {p.approach}</p>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
